import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getDashboard();
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={fetchDashboardStats}>
          Retry
        </button>
      </div>
    );
  }

  const planData = Object.entries(stats.users_by_plan || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">Overview of your Quorum platform</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{stats.total_users.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Today</div>
          <div className="stat-value">{stats.active_users_today.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active This Week</div>
          <div className="stat-value">{stats.active_users_week.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Conversations</div>
          <div className="stat-value">{stats.total_conversations.toLocaleString()}</div>
        </div>
      </div>

      {/* Query Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Queries Today</div>
          <div className="stat-value">{stats.total_queries_today.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Queries This Month</div>
          <div className="stat-value">{stats.total_queries_month.toLocaleString()}</div>
        </div>
        <div className="stat-card warning">
          <div className="stat-label">Errors (24h)</div>
          <div className="stat-value">{stats.error_count_24h.toLocaleString()}</div>
          <Link to="/errors" className="stat-link">View errors</Link>
        </div>
        <div className="stat-card danger">
          <div className="stat-label">Unresolved Errors</div>
          <div className="stat-value">{stats.unresolved_errors.toLocaleString()}</div>
          <Link to="/errors?unresolved=true" className="stat-link">View unresolved</Link>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        {/* Users by Plan */}
        <div className="chart-card">
          <h3 className="chart-title">Users by Plan</h3>
          {planData.length > 0 ? (
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={planData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {planData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="no-data">No plan data available</div>
          )}
          <div className="chart-legend">
            {planData.map((item, index) => (
              <div key={item.name} className="legend-item">
                <span className="legend-color" style={{ background: COLORS[index % COLORS.length] }}></span>
                <span className="legend-label">{item.name}</span>
                <span className="legend-value">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="chart-card">
          <h3 className="chart-title">Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/users" className="action-card">
              <div className="action-icon users-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="action-content">
                <div className="action-title">Manage Users</div>
                <div className="action-desc">View and manage user accounts</div>
              </div>
            </Link>

            <Link to="/usage" className="action-card">
              <div className="action-icon usage-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10" />
                  <line x1="12" y1="20" x2="12" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="14" />
                </svg>
              </div>
              <div className="action-content">
                <div className="action-title">Usage Analytics</div>
                <div className="action-desc">View usage statistics</div>
              </div>
            </Link>

            <Link to="/errors" className="action-card">
              <div className="action-icon errors-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="action-content">
                <div className="action-title">Error Logs</div>
                <div className="action-desc">Monitor and resolve errors</div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
